/**
 * Mirror of Move `payout_math` / `funded_first_trade` / `campaign_lock`
 * defaults; pinned value-for-value by `protected-amount.test.ts`.
 * Raw USDC (×10⁶) bigints (u64 parity); `BigInt(...)` over `n` literals — ES2017.
 */

/** Mirror of `campaign_lock::DEFAULT_MIN_DURATION_DAYS` / `DEFAULT_MAX_DURATION_DAYS`. */
export const MIN_DURATION_DAYS = 1;
export const MAX_DURATION_DAYS = 49;

/** One row of the campaign's tier table (`funded_first_trade::get_tier_config`). */
export interface TierSlateTier {
  durationDays: number;
  credits: number;
  tierRank: number;
  leverage: number;
}

/**
 * Mirror of `funded_first_trade::DEFAULT_TIER_{DURATIONS_DAYS,CREDITS_PER_TIER,RANKS,LEVERAGES}`.
 * A lock qualifies for the highest tier with `durationDays <= lock duration`.
 * Fallback only — `set_credit_tier_config` can change the live table, so
 * prefer `Eligibility.tierSlate`.
 */
export const TIER_SLATE: readonly TierSlateTier[] = [
  { durationDays: 1, credits: 1, tierRank: 1, leverage: 10 },
  { durationDays: 4, credits: 1, tierRank: 2, leverage: 20 },
  { durationDays: 7, credits: 1, tierRank: 3, leverage: 40 },
];

/** Compiled tier thresholds — matches `TIER_SLATE` defaults only. */
export const DURATION_DAYS_RAW = [1, 4, 7] as const;

/** Any valid lock duration; widened from `1 | 4 | 7` for runtime tier config. */
export type LockDurationDays = number;

/** Non-throwing mirror of `validateLockDuration`. */
export function isValidLockDuration(n: number): n is LockDurationDays {
  return Number.isInteger(n) && n >= MIN_DURATION_DAYS && n <= MAX_DURATION_DAYS;
}

/** Mirror of `payout_math`'s `DEFAULT_{LOW,HIGH}_{LOCK,PROTECTED}` anchors. */
const PAYOUT_LOW_LOCK = BigInt(250_000_000);
const PAYOUT_LOW_PROTECTED = BigInt(10_000_000);
const PAYOUT_HIGH_LOCK = BigInt(5_000_000_000);
const PAYOUT_HIGH_PROTECTED = BigInt(220_000_000);

export const MIN_LOCK_AMOUNT_RAW = PAYOUT_LOW_LOCK;
export const MAX_LOCK_AMOUNT_RAW = PAYOUT_HIGH_LOCK;

export interface PayoutAnchors {
  lowLock: bigint;
  lowProtected: bigint;
  highLock: bigint;
  highProtected: bigint;
}

export const DEFAULT_ANCHORS: PayoutAnchors = {
  lowLock: PAYOUT_LOW_LOCK,
  lowProtected: PAYOUT_LOW_PROTECTED,
  highLock: PAYOUT_HIGH_LOCK,
  highProtected: PAYOUT_HIGH_PROTECTED,
};

export class InvalidDurationError extends Error {
  constructor(durationDays: number) {
    super(`duration_days ${durationDays} outside [${MIN_DURATION_DAYS}, ${MAX_DURATION_DAYS}]`);
    this.name = "InvalidDurationError";
  }
}

/**
 * Mirror of `campaign_lock::assert_valid_duration` at default bounds.
 * Throws `InvalidDurationError` for non-integer or out-of-range days.
 */
export function validateLockDuration(days: number): void {
  if (!Number.isInteger(days) || days < MIN_DURATION_DAYS || days > MAX_DURATION_DAYS) {
    throw new InvalidDurationError(days);
  }
}

/** Mirror of `payout_math::compute`; evaluated on the user's TOTAL active locked principal, not per-lock. */
export function protectedAmountFor(
  activeLocked: bigint,
  anchors: PayoutAnchors = DEFAULT_ANCHORS,
): bigint {
  if (activeLocked < anchors.lowLock) {
    return BigInt(0);
  }
  if (activeLocked >= anchors.highLock) {
    return anchors.highProtected;
  }
  const span = anchors.highLock - anchors.lowLock;
  const range = anchors.highProtected - anchors.lowProtected;
  const delta = activeLocked - anchors.lowLock;
  return anchors.lowProtected + (range * delta) / span;
}

export interface CreditSlate {
  credits: number;
  tierRank: number;
  leverage: number;
}

/**
 * Mirror of `user_credits::credit_slate_for_duration_days`: highest tier whose
 * `durationDays <= days`, zeros below the first tier. Pass the live slate from
 * `Eligibility.tierSlate`; the compiled default is a loading-state fallback.
 */
export function creditSlateForDurationDays(
  days: number,
  slate: readonly TierSlateTier[] = TIER_SLATE,
): CreditSlate {
  let result: CreditSlate = { credits: 0, tierRank: 0, leverage: 0 };
  for (const tier of slate) {
    if (days >= tier.durationDays) {
      result = {
        credits: tier.credits,
        tierRank: tier.tierRank,
        leverage: tier.leverage,
      };
    }
  }
  return result;
}

/**
 * Per-credit trial position: `protected_amount × leverage_at_grant`, the
 * notional `funded_first_trade::open_trial` reserves per trial. Raw USDC.
 * Pass the live anchors/slate from `Eligibility`; defaults are a fallback.
 */
export function trialSizeFor(
  activeLocked: bigint,
  durationDays: number,
  anchors: PayoutAnchors = DEFAULT_ANCHORS,
  slate: readonly TierSlateTier[] = TIER_SLATE,
): bigint {
  validateLockDuration(durationDays);
  const { leverage } = creditSlateForDurationDays(durationDays, slate);
  return protectedAmountFor(activeLocked, anchors) * BigInt(leverage);
}
